import React, { useState } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { supabase } from "../../../lib/supabase";

const statusConfig = {
  ACTIVE: { label: "Activo", color: "bg-emerald-100 text-emerald-700", icon: "CheckCircle" },
  active: { label: "Activo", color: "bg-emerald-100 text-emerald-700", icon: "CheckCircle" },
  EXPIRED: { label: "Vencido", color: "bg-red-100 text-red-700", icon: "XCircle" },
  expired: { label: "Vencido", color: "bg-red-100 text-red-700", icon: "XCircle" },
  REVOKED: { label: "Revocado", color: "bg-gray-100 text-gray-600", icon: "Ban" },
  revoked: { label: "Revocado", color: "bg-gray-100 text-gray-600", icon: "Ban" },
  LIMIT_REACHED: { label: "Límite alcanzado", color: "bg-orange-100 text-orange-700", icon: "AlertCircle" },
  limit_reached: { label: "Límite alcanzado", color: "bg-orange-100 text-orange-700", icon: "AlertCircle" },
  pending: { label: "Pendiente", color: "bg-amber-100 text-amber-700", icon: "Clock" },
  completed: { label: "Completado", color: "bg-blue-100 text-blue-700", icon: "CheckCheck" },
};

const getEffectiveStatus = (row) => {
  if (row?.status === 'REVOKED' || row?.status === 'revoked') return 'REVOKED';
  if (row?.urlExpired) return 'EXPIRED';
  if (row?.limit > 0 && row?.downloads >= row?.limit) return 'LIMIT_REACHED';
  return row?.status?.toUpperCase() || 'ACTIVE';
};

const AssignmentHistoryTable = ({ newAssignments, onRegenerateUrl }) => {
  const [filterStatus, setFilterStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [revoking, setRevoking] = useState(null);
  const perPage = 8;

  const rows = newAssignments || [];

  const filtered = filterStatus === "all"
    ? rows
    : rows?.filter((r) => getEffectiveStatus(r) === filterStatus?.toUpperCase());

  const totalPages = Math.ceil(filtered?.length / perPage);
  const paginated = filtered?.slice((page - 1) * perPage, page * perPage);

  const handleRevoke = async (assignmentId) => {
    setRevoking(assignmentId);
    try {
      await supabase
        ?.from('update_assignments')
        ?.update({ status: 'REVOKED' })
        ?.eq('id', assignmentId);
      onRegenerateUrl?.(null, null); // trigger parent refresh
    } finally {
      setRevoking(null);
    }
  };

  const handleRegenerate = (row) => {
    onRegenerateUrl?.(row?.id, row?._raw?.updates?.file_path || row?.filePath);
  };

  const filterButtons = ["all", "ACTIVE", "EXPIRED", "REVOKED", "LIMIT_REACHED"];
  const filterLabels = { all: "Todos", ACTIVE: "Activo", EXPIRED: "Vencido", REVOKED: "Revocado", LIMIT_REACHED: "Límite" };

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: "var(--color-card)", boxShadow: "var(--shadow-md)" }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b" style={{ borderColor: "var(--color-border)" }}>
        <div className="flex items-center gap-2">
          <Icon name="History" size={18} color="var(--color-primary)" />
          <h2 className="font-heading font-bold text-base" style={{ color: "var(--color-foreground)" }}>Historial de Asignaciones & URLs Firmadas</h2>
          <span className="font-caption text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--color-muted)", color: "var(--color-muted-foreground)" }}>{filtered?.length} registros</span>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto">
          {filterButtons?.map((s) => (
            <button
              key={s}
              onClick={() => { setFilterStatus(s); setPage(1); }}
              className={`flex-shrink-0 font-caption text-xs px-3 py-1.5 rounded-full transition-base border`}
              style={filterStatus === s
                ? { background: "var(--color-primary)", borderColor: "var(--color-primary)", color: "#fff" }
                : { borderColor: "transparent", color: "var(--color-muted-foreground)" }
              }
            >
              {filterLabels?.[s]}
            </button>
          ))}
        </div>
      </div>
      {/* Desktop table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "var(--color-muted)" }}>
              {["Organización", "Actualización", "URL generada", "Vence el", "Descargas", "Estado", "Acciones"]?.map((h) => (
                <th key={h} className="font-caption text-xs font-semibold text-left px-4 py-2.5" style={{ color: "var(--color-muted-foreground)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated?.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center">
                  <Icon name="History" size={32} color="var(--color-muted-foreground)" />
                  <p className="font-caption text-sm mt-2" style={{ color: "var(--color-muted-foreground)" }}>No hay asignaciones registradas</p>
                </td>
              </tr>
            ) : (
              paginated?.map((row) => {
                const effectiveStatus = getEffectiveStatus(row);
                const sc = statusConfig?.[effectiveStatus] || statusConfig?.ACTIVE;
                const limitDisplay = row?.limit > 0 ? `${row?.downloads}/${row?.limit}` : `${row?.downloads}/∞`;
                const limitPct = row?.limit > 0 ? Math.min((row?.downloads / row?.limit) * 100, 100) : 0;
                return (
                  <tr key={row?.id} className="border-t hover:bg-gray-50 transition-base" style={{ borderColor: "var(--color-border)" }}>
                    <td className="px-4 py-3">
                      <p className="font-caption font-semibold text-sm" style={{ color: "var(--color-foreground)" }}>{row?.org}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-caption font-semibold text-sm" style={{ color: "var(--color-foreground)" }}>{row?.updateName}</p>
                      <p className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>v{row?.version}</p>
                    </td>
                    <td className="px-4 py-3">
                      {row?.signedUrl ? (
                        <span className="font-caption text-xs px-2 py-0.5 rounded" style={{ background: "rgba(15,118,110,0.1)", color: "var(--color-primary)" }}>URL activa</span>
                      ) : (
                        <span className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>Sin URL</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-caption text-xs whitespace-nowrap ${row?.urlExpired ? 'text-red-600 font-semibold' : ''}`} style={!row?.urlExpired ? { color: "var(--color-muted-foreground)" } : {}}>
                        {row?.expiresDate}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {row?.limit > 0 && (
                          <div className="w-14 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--color-muted)" }}>
                            <div className="h-full rounded-full" style={{ width: `${limitPct}%`, background: limitPct >= 100 ? "var(--color-success)" : "var(--color-primary)" }} />
                          </div>
                        )}
                        <span className="font-data text-xs whitespace-nowrap" style={{ color: "var(--color-muted-foreground)" }}>{limitDisplay}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-caption text-xs px-2 py-0.5 rounded-full ${sc?.color}`}>{sc?.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          className="p-1.5 rounded hover:bg-gray-100 transition-base"
                          title="Regenerar URL"
                          onClick={() => handleRegenerate(row)}
                          disabled={effectiveStatus === 'REVOKED'}
                        >
                          <Icon name="RefreshCw" size={14} color={effectiveStatus === 'REVOKED' ? "var(--color-muted-foreground)" : "var(--color-primary)"} />
                        </button>
                        <button
                          className="p-1.5 rounded hover:bg-gray-100 transition-base"
                          title="Revocar acceso"
                          onClick={() => handleRevoke(row?.id)}
                          disabled={effectiveStatus === 'REVOKED' || revoking === row?.id}
                        >
                          <Icon name="Ban" size={14} color={effectiveStatus === 'REVOKED' ? "var(--color-muted-foreground)" : "var(--color-error)"} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {/* Mobile cards */}
      <div className="lg:hidden divide-y" style={{ borderColor: "var(--color-border)" }}>
        {paginated?.map((row) => {
          const effectiveStatus = getEffectiveStatus(row);
          const sc = statusConfig?.[effectiveStatus] || statusConfig?.ACTIVE;
          return (
            <div key={row?.id} className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-caption font-semibold text-sm" style={{ color: "var(--color-foreground)" }}>{row?.updateName} <span style={{ color: "var(--color-muted-foreground)" }}>v{row?.version}</span></p>
                  <p className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>{row?.org}</p>
                </div>
                <span className={`font-caption text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${sc?.color}`}>{sc?.label}</span>
              </div>
              <div className="flex items-center gap-4 text-xs font-caption" style={{ color: "var(--color-muted-foreground)" }}>
                <span>Asignado: {row?.assignedDate}</span>
                <span className={row?.urlExpired ? 'text-red-600 font-semibold' : ''}>Vence: {row?.expiresDate}</span>
                <span>{row?.downloads}/{row?.limit || '∞'} desc.</span>
              </div>
              <div className="flex gap-2">
                <button className="flex items-center gap-1 text-xs font-caption px-2 py-1 rounded border" style={{ borderColor: "var(--color-border)", color: "var(--color-primary)" }} onClick={() => handleRegenerate(row)} disabled={effectiveStatus === 'REVOKED'}>
                  <Icon name="RefreshCw" size={12} color="currentColor" /> Regenerar
                </button>
                <button className="flex items-center gap-1 text-xs font-caption px-2 py-1 rounded border" style={{ borderColor: "var(--color-border)", color: "var(--color-error)" }} onClick={() => handleRevoke(row?.id)} disabled={effectiveStatus === 'REVOKED'}>
                  <Icon name="Ban" size={12} color="currentColor" /> Revocar
                </button>
              </div>
            </div>
          );
        })}
        {paginated?.length === 0 && (
          <div className="p-8 text-center">
            <Icon name="History" size={32} color="var(--color-muted-foreground)" />
            <p className="font-caption text-sm mt-2" style={{ color: "var(--color-muted-foreground)" }}>No hay asignaciones</p>
          </div>
        )}
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: "var(--color-border)" }}>
          <span className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>Página {page} de {totalPages}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)} iconName="ChevronLeft" iconPosition="left">Anterior</Button>
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} iconName="ChevronRight" iconPosition="right">Siguiente</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentHistoryTable;